// Localization for system-generated contract-history text.
//
// Freshly composed timeline events carry `key`/`descKey` + `params` from the
// API and are rendered straight from the locale files (localizeTimelineEvent).
// Audit rows and version change-descriptions are stored in the database as
// finished English sentences, so those are pattern-matched back into key +
// params here (localizeSystemText) — covering historical data too. Dynamic
// values (contract/document/workflow/user names) pass through untouched,
// except seeded bilingual "TR / EN" names which collapse to the viewer's half.
import { localizeSeeded } from '../pages/FlowManagement/flowUtils';

const P = (re, key, map) => ({ re, key, map });

// Ordered most-specific first; the broad `X signed "Y"` matcher stays last.
const PATTERNS = [
  P(/^Contract "(.+)" archived\/deleted$/, 'auditContractDeleted', (m) => ({ name: m[1] })),
  P(/^Document "(.+)" uploaded to contract "(.+)"$/, 'auditDocUploaded', (m) => ({ doc: m[1], contract: m[2] })),
  P(/^Document "(.+)" downloaded from contract "(.+)"$/, 'auditDocDownloaded', (m) => ({ doc: m[1], contract: m[2] })),
  P(/^Changes requested at step "(.+)": ([\s\S]*)$/, 'auditChangesRequested', (m) => ({ step: m[1], comments: m[2] })),
  P(/^Returned to revision from the signature step: ([\s\S]*)$/, 'auditReturnedToRevision', (m) => ({ comments: m[1] })),
  P(/^Signature requested for "(.+)" from (.+)$/, 'auditSignatureRequested', (m) => ({ name: m[1], emails: m[2] })),
  P(/^All signatures completed - contract fully executed$/, 'auditAllSignaturesCompleted', () => ({})),
  P(/^Signing initiated for (\d+) document\(s\)$/, 'auditSigningInitiated', (m) => ({ num: m[1] })),
  P(/^Signed copy uploaded — contract "(.+)" marked as fully executed$/, 'auditSignedCopyUploaded', (m) => ({ contract: m[1] })),
  P(/^Signing request cancelled$/, 'auditSigningCancelled', () => ({})),
  P(/^DocuSign envelope sent for "(.+)" \((.+)\)$/, 'auditDocusignSent', (m) => ({ name: m[1], envelope: m[2] })),
  P(/^DocuSign envelope (.+) completed\. Contract fully executed\.$/, 'auditDocusignCompleted', (m) => ({ envelope: m[1] })),
  P(/^Approval step "(.+)" timed out — assignee\(s\) notified$/, 'auditStepTimeout', (m) => ({ step: m[1] })),
  P(/^Approval step "(.+)" timed out and was escalated to (\d+) user\(s\)$/, 'auditStepEscalated', (m) => ({ step: m[1], num: m[2] })),
  P(/^Approval step "(.+)" auto-approved after timeout$/, 'auditStepAutoApproved', (m) => ({ step: m[1] })),
  P(/^Approval step "(.+)" auto-rejected after timeout — workflow rejected$/, 'auditStepAutoRejected', (m) => ({ step: m[1] })),
  P(/^Auto-rejected: step timed out \(escalation policy\)$/, 'autoRejectedTimeout', () => ({})),
  P(/^Contract updated$/, 'contractUpdated', () => ({})),
  // Change History document events (recordDocumentChange on the backend).
  P(/^Supporting document "(.+)" added$/, 'docSupportingAdded', (m) => ({ doc: m[1] })),
  P(/^Supporting document "(.+)" removed$/, 'docSupportingRemoved', (m) => ({ doc: m[1] })),
  P(/^Contract document "(.+)" added$/, 'docContractAdded', (m) => ({ doc: m[1] })),
  P(/^Signed copy "(.+)" uploaded$/, 'docSignedUploaded', (m) => ({ doc: m[1] })),
  P(/^Version (\d+) of document "(.+)" uploaded$/, 'docVersionUploaded', (m) => ({ num: m[1], doc: m[2] })),
  P(/^Document "(.+)" removed$/, 'docRemoved', (m) => ({ doc: m[1] })),
  P(/^Signing date updated at the signed-contract step$/, 'signingDateUpdated', () => ({})),
  P(/^Verification QR generated for document "(.+)" \(v(\d+)\)$/, 'auditQrGenerated', (m) => ({ doc: m[1], version: m[2] })),
  P(/^QR verification on signed upload: (\w+)$/, 'auditQrChecked', (m) => ({ status: m[1] })),
  P(/^QR verification override \((\w+)\): ([\s\S]*)$/, 'auditQrOverridden', (m) => ({ status: m[1], reason: m[2] })),
  P(/^(.+) signed "(.+)"$/, 'auditSignerSigned', (m) => ({ name: m[1], doc: m[2] })),
];

const localizeParams = (params, lang) => {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    // Step/workflow names arrive as raw string-or-multilang values — resolve
    // objects to the viewer's language (falling back across languages), and
    // collapse legacy seeded "TR / EN" strings to the viewer's half.
    out[k] = (typeof v === 'string' || (v && typeof v === 'object'))
      ? localizeSeeded(v, lang)
      : v;
  }
  return out;
};

// Translate a stored English system sentence; unrecognized text (user content,
// legacy shapes) falls back to seeded-name localization only.
export function localizeSystemText(text, t, lang) {
  if (!text || typeof text !== 'string') return text;
  for (const { re, key, map } of PATTERNS) {
    const m = text.match(re);
    if (m) {
      return t(`contractTimeline.events.${key}`, { ...localizeParams(map(m), lang), defaultValue: text });
    }
  }
  return localizeSeeded(text, lang);
}

// Render a timeline event's title or description in the viewer's language.
export function localizeTimelineEvent(ev, field, t, lang) {
  const key = field === 'title' ? ev.key : ev.descKey;
  const raw = field === 'title' ? ev.title : ev.description;
  if (key) {
    return t(`contractTimeline.events.${key}`, { ...localizeParams(ev.params, lang), defaultValue: raw });
  }
  return localizeSystemText(raw, t, lang);
}
